import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Package,
  DollarSign,
  Clock,
  Users,
  Star,
  Search,
  ChevronDown,
  ChevronUp,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { useToastContext } from '../../../contexts/ToastContext';
import { useMyServicePackages, useCreateVendorServicePackage, useUpdateVendorServicePackage, useDeleteVendorServicePackage, useTogglePackageStatus } from '../../../services/vendorServicePackageService';
import { useMyServices } from '../../../services/vendorServiceService';
import Button from '../../../components/shared/Button';
import Input from '../../../components/shared/Input';
import SearchSelect from '../../../components/shared/SearchSelect';
import { ServicePackageModal } from '../../../components/vendor';
import {
  validatePaymentScheduleRules,
  parsePaymentScheduleRulesFromPackage,
  isPlatformDefaultPaymentRules,
} from '../../../utils/paymentTerms';
import { VendorPageHeader, VendorGuideCallout } from '../../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../../utils/vendorPageGuides';

/** Map vendor service starting price into two-decimal string for package base price. */
function formatServiceStartingPrice(service) {
  if (!service || service.base_price == null || service.base_price === '') return '';
  const n =
    typeof service.base_price === 'number'
      ? service.base_price
      : parseFloat(String(service.base_price), 10);
  if (Number.isNaN(n)) return '';
  return n.toFixed(2);
}

const VendorServicePackages = () => {
  const { success, error: showError } = useToastContext();
  
  const [modalState, setModalState] = useState({
    isOpen: false,
    isEdit: false,
    selectedPackage: null,
    selectedService: null
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedServices, setExpandedServices] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    package_name: '',
    image_url: '',
    description: '',
    service_id: '',
    duration_hours: '1',
    base_price: '0.00',
    price_type: 'fixed',
    min_duration_hours: '',
    max_duration_hours: '',
    setup_time_minutes: '',
    teardown_time_minutes: '',
    max_guests: '1',
    includes_items: [],
    addon_options: [],
    package_features: [],
    payment_schedule_rules: { use_platform_default: true },
    is_popular: false,
    is_active: true
  });

  // Fetch services data
  const { data: servicesData, isLoading: servicesLoading } = useMyServices();
  const services = useMemo(
    () => (Array.isArray(servicesData?.data?.data) ? servicesData.data.data : []),
    [servicesData?.data?.data]
  );

  // Fetch packages data
  const { data: packagesData, isLoading: packagesLoading, refetch } = useMyServicePackages();
  const packages = useMemo(
    () => (Array.isArray(packagesData?.data?.data) ? packagesData.data.data : []),
    [packagesData?.data?.data]
  );

  const { data: statusResponse, isLoading: subscriptionLoading } = useVendorSubscriptionStatus();

  const hasActiveSubscription = Boolean(statusResponse?.data?.has_active_subscription);
  const subscriptionReady = !subscriptionLoading;
  const usageLimits = statusResponse?.data?.current_subscription?.limits || {};
  const maxPackagesRaw = usageLimits?.max_packages_per_service;
  const maxPackagesPerService =
    maxPackagesRaw != null && maxPackagesRaw !== ''
      ? Number(maxPackagesRaw)
      : null;

  const packagesCountForService = useCallback(
    (serviceId) => {
      if (!serviceId) return 0;
      return packages.filter((p) => p.service_id === serviceId).length;
    },
    [packages]
  );

  const serviceAtPackageLimit = useCallback(
    (serviceId) =>
      maxPackagesPerService != null &&
      packagesCountForService(serviceId) >= maxPackagesPerService,
    [maxPackagesPerService, packagesCountForService]
  );

  const anyServiceCanAddPackage = useMemo(() => {
    if (maxPackagesPerService == null) return true;
    if (!services.length) return false;
    return services.some((s) => !serviceAtPackageLimit(s.id));
  }, [maxPackagesPerService, services, serviceAtPackageLimit]);

  const allServicesAtPackageLimit = useMemo(() => {
    if (!services.length || maxPackagesPerService == null) return false;
    return services.every((s) => serviceAtPackageLimit(s.id));
  }, [services, maxPackagesPerService, serviceAtPackageLimit]);

  // Mutations
  const createPackageMutation = useCreateVendorServicePackage({
    onSuccess: () => {
      success('Package created successfully!');
      closeModal();
      resetForm();
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to create package');
    }
  });

  const updatePackageMutation = useUpdateVendorServicePackage({
    onSuccess: () => {
      success('Package updated successfully!');
      closeModal();
      resetForm();
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update package');
    }
  });

  const deletePackageMutation = useDeleteVendorServicePackage({
    onSuccess: () => {
      success('Package deleted successfully!');
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to delete package');
    }
  });

  const toggleStatusMutation = useTogglePackageStatus({
    onSuccess: () => {
      success('Package status updated successfully!');
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update package status');
    }
  });

  // Filter packages
  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.package_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesService = !selectedService || pkg.service_id === selectedService;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && pkg.is_active) ||
                         (statusFilter === 'inactive' && !pkg.is_active);
    
    return matchesSearch && matchesService && matchesStatus;
  });

  // Group packages by service
  const groupedPackages = filteredPackages.reduce((acc, pkg) => {
    if (!acc[pkg.service_id]) {
      const service = services.find(s => s.id === pkg.service_id);
      acc[pkg.service_id] = {
        service: service || { id: pkg.service_id, service_name: 'Unknown Service' },
        packages: []
      };
    }
    acc[pkg.service_id].packages.push(pkg);
    return acc;
  }, {});

  const resetForm = () => {
    setFormData({
      package_name: '',
      image_url: '',
      description: '',
      service_id: '',
      duration_hours: '1',
      base_price: '',
      price_type: 'fixed',
      min_duration_hours: '',
      max_duration_hours: '',
      setup_time_minutes: '',
      teardown_time_minutes: '',
      max_guests: '1',
      includes_items: [],
      addon_options: [],
      package_features: [],
      payment_schedule_rules: { use_platform_default: true },
      is_popular: false,
      is_active: true
    });
  };

  const openCreateModal = (service = null) => {
    setModalState({
      isOpen: true,
      isEdit: false,
      selectedPackage: null,
      selectedService: service,
    });
    resetForm();
    if (service?.id) {
      const priceStr = formatServiceStartingPrice(service);
      setFormData((prev) => ({
        ...prev,
        service_id: service.id,
        base_price: priceStr !== '' ? priceStr : '',
      }));
    }
  };

  // Helper function to safely convert value to array
  const ensureArray = (value) => {
    if (Array.isArray(value)) {
      return value;
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // If it's not valid JSON, treat as empty array
        return [];
      }
    }
    // If it's an object, try to convert to array
    if (typeof value === 'object') {
      return Object.values(value).filter(v => v !== null && v !== undefined);
    }
    return [];
  };

  const openEditModal = (packageItem) => {
    setModalState({ 
      isOpen: true, 
      isEdit: true, 
      selectedPackage: packageItem,
      selectedService: null
    });
    setFormData({
      package_name: packageItem.package_name || '',
      image_url: packageItem.image_url || '',
      description: packageItem.description || '',
      service_id: packageItem.service_id || '',
      duration_hours: packageItem.duration_hours || '1',
      base_price: packageItem.base_price || '',
      price_type: 'fixed',
      min_duration_hours: packageItem.min_duration_hours || '',
      max_duration_hours: packageItem.max_duration_hours || '',
      setup_time_minutes: packageItem.setup_time_minutes || '',
      teardown_time_minutes: packageItem.teardown_time_minutes || '',
      max_guests: packageItem.max_guests || '1',
      includes_items: ensureArray(packageItem.includes_items),
      addon_options: ensureArray(packageItem.addon_options),
      package_features: ensureArray(packageItem.package_features),
      payment_schedule_rules: parsePaymentScheduleRulesFromPackage(
        packageItem.payment_schedule_rules
      ),
      is_popular: packageItem.is_popular || false,
      is_active: packageItem.is_active !== undefined ? packageItem.is_active : true
    });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, isEdit: false, selectedPackage: null, selectedService: null });
    resetForm();
  };

  const handleCreate = () => {
    if (services.length === 0) {
      showError('You need to create services first before adding packages.');
      return;
    }
    if (subscriptionReady && !hasActiveSubscription) {
      showError('You need an active subscription to add packages.');
      return;
    }
    if (allServicesAtPackageLimit) {
      showError(
        'Package limit reached for every service on your plan. Upgrade your subscription to add more packages per service.'
      );
      return;
    }
    openCreateModal();
  };

  const handleEdit = (packageItem) => {
    openEditModal(packageItem);
  };

  const handleDelete = (packageItem) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      deletePackageMutation.mutate(packageItem.id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !modalState.isEdit &&
      formData.service_id &&
      maxPackagesPerService != null &&
      packagesCountForService(formData.service_id) >= maxPackagesPerService
    ) {
      showError(
        `Your plan allows up to ${maxPackagesPerService} packages per service. Upgrade your subscription to add more.`
      );
      return;
    }
    
    const rulesValidation = validatePaymentScheduleRules(formData.payment_schedule_rules);
    if (!rulesValidation.valid) {
      showError(rulesValidation.errors[0] || 'Invalid payment schedule rules');
      return;
    }

    const paymentScheduleRules = isPlatformDefaultPaymentRules(formData.payment_schedule_rules)
      ? { use_platform_default: true }
      : rulesValidation.normalized;

    // Format form data to ensure proper types and handle empty values
    const formattedData = {
      ...formData,
      price_type: 'fixed',
      duration_hours: formData.duration_hours ? parseFloat(formData.duration_hours) : 1.0,
      base_price: formData.base_price ? parseFloat(formData.base_price) : 0.0,
      min_duration_hours: formData.min_duration_hours ? parseFloat(formData.min_duration_hours) : null,
      max_duration_hours: formData.max_duration_hours ? parseFloat(formData.max_duration_hours) : null,
      setup_time_minutes: formData.setup_time_minutes ? parseInt(formData.setup_time_minutes) : null,
      teardown_time_minutes: formData.teardown_time_minutes ? parseInt(formData.teardown_time_minutes) : null,
      max_guests: formData.max_guests ? parseInt(formData.max_guests) : 1,
      payment_schedule_rules: paymentScheduleRules,
    };
    
    if (modalState.isEdit) {
      updatePackageMutation.mutate({ id: modalState.selectedPackage.id, data: formattedData });
    } else {
      createPackageMutation.mutate(formattedData);
    }
  };

  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => {
        let next = { ...prev, [field]: value };
        if (field === 'service_id' && !modalState.isEdit && value) {
          const svc = services.find((s) => s.id === value);
          const priceStr = formatServiceStartingPrice(svc);
          next = { ...next, base_price: priceStr !== '' ? priceStr : '' };
        }
        return next;
      });
    },
    [services, modalState.isEdit]
  );

  const handleImageSelect = useCallback((image) => {
    if (image) {
      handleInputChange('image_url', image.image_url);
    }
  }, [handleInputChange]);

  const handleArrayItemChange = useCallback((field, index, value) => {
    setFormData(prev => {
      const newItems = [...prev[field]];
      newItems[index] = value;
      return {
        ...prev,
        [field]: newItems
      };
    });
  }, []);

  const handleAddArrayItem = useCallback((field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  }, []);

  const handleRemoveArrayItem = useCallback((field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  }, []);

  const handlePaymentScheduleRulesChange = useCallback((rules) => {
    setFormData((prev) => ({ ...prev, payment_schedule_rules: rules }));
  }, []);

  const toggleServiceExpansion = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const handleToggleStatus = (packageItem) => {
    toggleStatusMutation.mutate(packageItem.id);
  };

  if (packagesLoading || servicesLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading packages...</p>
        </div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
          <p className="text-gray-600 mb-6">
            You need to create services first before adding packages.
          </p>
          <Button onClick={() => window.location.href = '/vendor/services'}>
            Create Services
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
        <VendorPageHeader
          title="Service Packages"
          subtitle="Bundle services into packages clients can choose at checkout on your public profile."
          actions={
            <Button
              onClick={handleCreate}
              icon={Plus}
              className="w-full sm:w-auto"
              disabled={
                subscriptionReady &&
                (!hasActiveSubscription ||
                  (maxPackagesPerService != null && services.length > 0 && !anyServiceCanAddPackage))
              }
            >
              Add New Package
            </Button>
          }
        />

        <VendorGuideCallout
          title={VENDOR_PAGE_GUIDES.packages.title}
          steps={VENDOR_PAGE_GUIDES.packages.steps}
          icon={ListChecks}
        />

        {subscriptionReady && !hasActiveSubscription && (
          <p className="text-sm text-amber-800 mb-3">
            An active subscription is required to manage packages.{' '}
            <Link to="/vendor/subscription" className="font-medium underline">
              View plans
            </Link>
          </p>
        )}
        {subscriptionReady && hasActiveSubscription && allServicesAtPackageLimit && (
          <p className="text-sm text-amber-800 mb-3">
            Each of your services already has the maximum number of packages allowed on your plan ({maxPackagesPerService}{' '}
            per service).{' '}
            <Link to="/vendor/subscription" className="font-medium underline">
              Upgrade
            </Link>{' '}
            for a higher limit.
          </p>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
              <SearchSelect
                value={selectedService}
                onChange={setSelectedService}
                options={[
                  { value: '', label: 'All Services' },
                  ...services.map(service => ({
                    value: service.id,
                    label: service.service_name
                  }))
                ]}
                placeholder="Filter by service"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <SearchSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Filter by status"
              />
            </div>
          </div>
        </div>

        {/* Packages by Service */}
        {Object.keys(groupedPackages).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Packages Found</h3>
            <p className="text-gray-600 mb-6">
              {packages.length === 0 
                ? "You haven't created any packages yet. Get started by adding your first package!"
                : "No packages match your current filters. Try adjusting your search criteria."
              }
            </p>
            {packages.length === 0 && (
              <Button
                onClick={handleCreate}
                icon={Plus}
                disabled={
                  subscriptionReady &&
                  (!hasActiveSubscription ||
                    (maxPackagesPerService != null && services.length > 0 && !anyServiceCanAddPackage))
                }
              >
                Create Your First Package
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedPackages).map(([serviceId, serviceData]) => (
              <div key={serviceId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Service Header */}
                <div 
                  className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleServiceExpansion(serviceId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Service Image */}
                      {serviceData.service.image_url ? (
                        <div className="flex-shrink-0">
                          <img
                            src={serviceData.service.image_url}
                            alt={serviceData.service.service_name}
                            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                          />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gray-200 border border-gray-300 flex items-center justify-center">
                          <Package className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {serviceData.service.service_name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {serviceData.service.service_category?.category_name || 'No Category'} •{' '}
                          {serviceData.packages.length} packages
                          {maxPackagesPerService != null && (
                            <span className="text-gray-500">
                              {' '}
                              (max {maxPackagesPerService} per service on your plan)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openCreateModal(serviceData.service);
                        }}
                        size="sm"
                        icon={Plus}
                        variant="outline"
                        disabled={
                          subscriptionReady &&
                          (!hasActiveSubscription || serviceAtPackageLimit(serviceId))
                        }
                        title={
                          serviceAtPackageLimit(serviceId)
                            ? `Plan allows up to ${maxPackagesPerService} packages per service`
                            : undefined
                        }
                      >
                        Add Package
                      </Button>
                      {expandedServices[serviceId] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Packages List */}
                {expandedServices[serviceId] && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {serviceData.packages.map((packageItem) => (
                        <div key={packageItem.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                          {/* Package Image */}
                          {packageItem.image_url && (
                            <div className="aspect-w-16 aspect-h-9 bg-gray-100 h-32">
                              <img
                                src={packageItem.image_url}
                                alt={packageItem.package_name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="text-md font-semibold text-gray-900">
                                {packageItem.package_name}
                              </h4>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              packageItem.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {packageItem.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {packageItem.description}
                          </p>
                          
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-sm text-gray-600">
                              <DollarSign className="w-4 h-4 mr-2" />
                              <span className="font-medium">${packageItem.base_price}</span>
                            </div>
                            
                            {packageItem.duration_hours && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Clock className="w-4 h-4 mr-2" />
                                <span>{packageItem.duration_hours} hours</span>
                              </div>
                            )}
                            
                            {packageItem.max_guests && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Users className="w-4 h-4 mr-2" />
                                <span>Up to {packageItem.max_guests} guests</span>
                              </div>
                            )}
                            
                            {packageItem.is_popular && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Star className="w-4 h-4 mr-2 text-yellow-500" />
                                <span className="text-yellow-600 font-medium">Popular Package</span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-3 border-t border-gray-200 gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <Button
                                onClick={() => handleEdit(packageItem)}
                                variant="outline"
                                size="sm"
                                icon={Edit}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleToggleStatus(packageItem)}
                                variant="outline"
                                size="sm"
                                icon={packageItem.is_active ? EyeOff : Eye}
                                className={packageItem.is_active 
                                  ? "text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                                  : "text-green-600 border-green-600 hover:bg-green-50"
                                }
                                disabled={toggleStatusMutation.isPending}
                              >
                                {packageItem.is_active ? 'Hide' : 'Show'}
                              </Button>
                            </div>
                            
                            <Button
                              onClick={() => handleDelete(packageItem)}
                              variant="outline"
                              size="sm"
                              icon={Trash2}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Service Package Modal */}
        <ServicePackageModal
          isOpen={modalState.isOpen}
          isEdit={modalState.isEdit}
          formData={formData}
          services={services}
          selectedService={modalState.selectedService}
          onClose={closeModal}
          onSubmit={handleSubmit}
          onInputChange={handleInputChange}
          onImageSelect={handleImageSelect}
          onArrayFieldChange={handleArrayItemChange}
          onAddArrayItem={handleAddArrayItem}
          onRemoveArrayItem={handleRemoveArrayItem}
          onPaymentScheduleRulesChange={handlePaymentScheduleRulesChange}
          isLoading={createPackageMutation.isPending || updatePackageMutation.isPending}
          maxPackagesPerService={maxPackagesPerService}
          existingPackages={packages}
          aiAccess={{
            enabled: Boolean(statusResponse?.data?.current_subscription?.features?.feature_ai_generator),
            limit: statusResponse?.data?.current_subscription?.limits?.max_ai_generations_per_month ?? null,
            used: statusResponse?.data?.current_subscription?.usage?.ai_generations_this_month ?? 0,
          }}
        />
    </div>
  );
};

export default VendorServicePackages;
